import React from 'react';
import { useState, useEffect, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LayoutDashboard, Users, GraduationCap, BookOpen, ClipboardCheck, Wallet, Calendar, Settings, LogOut, Menu, X, ChevronDown, Bell, Globe, DollarSign, Smartphone, Shield, UserCog, Receipt, TrendingDown, FolderCog } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import api from '../lib/api';

// Contexte pour les permissions
const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    return { hasPermission: () => true, canView: () => true, canCreate: () => false, canEdit: () => false, canDelete: () => false, dataScope: 'all' };
  }
  return context;
};

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [dataScope, setDataScope] = useState('all');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();

  // Charger les permissions de l'utilisateur
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const res = await api.get('/auth/me/permissions');
        setUserPermissions(res.data.permissions || []);
        setDataScope(res.data.data_scope || 'all');
      } catch (error) {
        // Permissions par défaut granulaires
        const defaultPerms = {
          admin: ['dashboard.view', 'students.view', 'students.create', 'students.edit', 'students.delete', 'teachers.view', 'teachers.create', 'classes.view', 'classes.create', 'evaluations.view', 'evaluations.create', 'bulletins.view', 'attendance.view', 'attendance.create', 'quran.view', 'payments.view', 'payments.create', 'internat.view', 'medical.view', 'settings.view', 'users.view', 'users.create', 'roles.view', 'roles.create'],
          supervisor: ['dashboard.view', 'students.view', 'students.create', 'teachers.view', 'classes.view', 'evaluations.view', 'evaluations.create', 'bulletins.view', 'attendance.view', 'attendance.create', 'quran.view'],
          teacher: ['dashboard.view', 'students.view', 'classes.view', 'evaluations.view', 'evaluations.create', 'attendance.view', 'attendance.create', 'quran.view', 'quran.create'],
          parent: ['dashboard.view', 'students.view', 'evaluations.view', 'bulletins.view', 'payments.view'],
          doctor: ['dashboard.view', 'students.view', 'medical.view', 'medical.create', 'internat.view'],
          accountant: ['dashboard.view', 'students.view', 'payments.view', 'payments.create'],
          cashier: ['dashboard.view', 'payments.view', 'payments.create'],
        };
        setUserPermissions(defaultPerms[user?.role] || ['dashboard.view']);
        setDataScope(user?.role === 'parent' ? 'own' : user?.role === 'teacher' ? 'assigned' : 'all');
      }
    };
    if (user) loadPermissions();
  }, [user]);

  // Vérifier si l'utilisateur a une permission spécifique (module.action)
  const hasPermission = (permission) => {
    if (user?.role === 'admin') return true;
    return userPermissions.some(p => p === permission || p.startsWith(permission + '.'));
  };

  // Vérifier si peut voir un module
  const canView = (module) => {
    if (user?.role === 'admin') return true;
    return userPermissions.some(p => p === `${module}.view` || p === module);
  };

  // Vérifier si peut créer dans un module
  const canCreate = (module) => {
    if (user?.role === 'admin') return true;
    return userPermissions.includes(`${module}.create`);
  };

  // Vérifier si peut modifier dans un module
  const canEdit = (module) => {
    if (user?.role === 'admin') return true;
    return userPermissions.includes(`${module}.edit`);
  };

  // Vérifier si peut supprimer dans un module
  const canDelete = (module) => {
    if (user?.role === 'admin') return true;
    return userPermissions.includes(`${module}.delete`);
  };

  const allNavigation = [
    { name: t('dashboard'), href: '/', icon: LayoutDashboard, module: 'dashboard' },
    { name: t('students'), href: '/students', icon: Users, module: 'students' },
    { name: 'Historique Élèves', href: '/students-history', icon: Users, module: 'students' },
    { name: t('teachers'), href: '/teachers', icon: GraduationCap, module: 'teachers' },
    { name: t('classes'), href: '/classes', icon: GraduationCap, module: 'classes' },
    { name: t('quranProgress'), href: '/quran-progress', icon: BookOpen, module: 'quran' },
    { name: 'Éval. Quotidienne', href: '/quranic-daily-eval', icon: ClipboardCheck, module: 'evaluations' },
    { name: 'Éval. Mensuelle', href: '/monthly-eval', icon: Calendar, module: 'evaluations' },
    { name: 'Éval. Trimestrielle', href: '/trimestrial-eval', icon: BookOpen, module: 'evaluations' },
    { name: 'Stats Évaluations', href: '/eval-dashboard', icon: LayoutDashboard, module: 'evaluations' },
    { name: t('evaluations'), href: '/evaluations', icon: ClipboardCheck, module: 'evaluations' },
    { name: t('grades'), href: '/grades', icon: ClipboardCheck, module: 'evaluations' },
    { name: t('bulletins'), href: '/bulletins', icon: ClipboardCheck, module: 'bulletins' },
    { name: t('attendance'), href: '/attendance', icon: Calendar, module: 'attendance' },
    { name: t('payments'), href: '/payments', icon: Wallet, module: 'payments' },
    { name: t('mobilePayments'), href: '/mobile-payments', icon: Smartphone, module: 'payments', requireCreate: true },
    { name: t('boardingFees'), href: '/boarding-fees', icon: DollarSign, module: 'payments', requireCreate: true },
    { name: 'Dépenses', href: '/expenses', icon: TrendingDown, module: 'payments', requireCreate: true },
    { name: 'Bulletins Salaire', href: '/salary-slips', icon: Receipt, module: 'payments', requireCreate: true },
    { name: 'Catégories Dépenses', href: '/expense-categories', icon: FolderCog, module: 'settings' },
    { name: t('schoolYears'), href: '/school-years', icon: Calendar, module: 'settings' },
    { name: t('internat'), href: '/internat', icon: Users, module: 'internat' },
    { name: t('parentPortal'), href: '/parent-portal', icon: Users, module: 'students', parentOnly: true },
    { name: 'Utilisateurs', href: '/admin/users', icon: UserCog, module: 'users' },
    { name: 'Rôles & Permissions', href: '/admin/roles', icon: Shield, module: 'roles' },
    { name: t('settings'), href: '/settings', icon: Settings, module: 'settings' },
  ];

  // Filtrer la navigation selon les permissions
  const navigation = allNavigation.filter(item => {
    // Vérifier si l'utilisateur peut voir le module
    if (!canView(item.module)) return false;
    
    // Si requireCreate est true, vérifier que l'utilisateur a la permission de créer
    if (item.requireCreate && !canCreate(item.module)) return false;
    
    // Si parentOnly, ne montrer qu'au parent
    if (item.parentOnly && user?.role !== 'parent') return false;
    
    return true;
  });

  const handleLogout = () => { logout(); navigate('/login'); };
  const getInitials = (fn, ln) => ((fn?.[0] || '') + (ln?.[0] || '')).toUpperCase();
  const getRoleBadgeColor = (r) => ({ admin: 'bg-primary-100 text-primary-700', direction: 'bg-secondary-100 text-secondary-700', teacher: 'bg-accent-100 text-accent-700', accounting: 'bg-amber-100 text-amber-700', parent: 'bg-blue-100 text-blue-700' }[r] || 'bg-gray-100 text-gray-700');
  const getRoleLabel = (r) => ({ admin: 'Administrateur', direction: 'Direction', teacher: 'Enseignant', accounting: 'Comptabilité', parent: 'Parent', doctor: 'Médecin', supervisor: 'Superviseur', cashier: 'Caissier', accountant: 'Comptable' }[r] || r);

  // Valeur du contexte des permissions
  const permissionsValue = { hasPermission, canView, canCreate, canEdit, canDelete, dataScope, userPermissions };

  return (
    <PermissionsContext.Provider value={permissionsValue}>
    <div className="min-h-screen bg-background" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {sidebarOpen && (<div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />)}
      
      <aside className={cn("fixed left-0 top-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-300 lg:translate-x-0 flex flex-col", sidebarOpen ? "translate-x-0" : "-translate-x-full")}>
        <div className="flex h-16 items-center justify-between px-4 border-b border-border shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="CARFEIS" className="h-10 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="font-heading text-lg font-bold text-primary">CARFEIS</span>
          </Link>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}><X className="h-5 w-5" /></Button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
              return (<li key={item.href}><Link to={item.href} className={cn("flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200", isActive ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:bg-muted hover:text-foreground")} onClick={() => setSidebarOpen(false)}><Icon className="h-5 w-5" />{item.name}</Link></li>);
            })}
          </ul>
        </nav>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card/95 backdrop-blur px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="h-5 w-5" /></Button>
            <div className="hidden md:block"><h1 className="text-xl font-heading font-semibold text-foreground">{navigation.find((n) => location.pathname === n.href || (n.href !== '/' && location.pathname.startsWith(n.href)))?.name || 'Dashboard'}</h1></div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={toggleLanguage} title={language === 'fr' ? 'العربية' : 'Français'}><Globe className="h-5 w-5" /><span className="sr-only">{language === 'fr' ? 'AR' : 'FR'}</span></Button>
            <Button variant="ghost" size="icon" className="relative"><Bell className="h-5 w-5" /><span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" /></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2">
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-primary text-sm">{getInitials(user?.first_name, user?.last_name)}</AvatarFallback></Avatar>
                  <div className="hidden md:block text-left"><p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p><p className="text-xs text-muted-foreground">{getRoleLabel(user?.role)}</p></div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5"><p className="text-sm font-medium">{user?.email}</p><span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1", getRoleBadgeColor(user?.role))}>{getRoleLabel(user?.role)}</span></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive"><LogOut className="mr-2 h-4 w-4" />Déconnexion</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
    </PermissionsContext.Provider>
  );
}

export default Layout;
